import { useCallback, useEffect, useState } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/lib/api";
import { fmtDate, formatApiError } from "@/lib/utils";
import { Sparkles, Loader2, CheckCircle2, ArrowLeft, MessageCircle, Gem, Send } from "lucide-react";

/**
 * Public upsell page — arrived at from the WhatsApp template CTA
 * ({{4}} = /g/:eventId/originals?ref=<lookup_id>).
 *
 * The guest sees a warm, on-brand pitch for the high-resolution album and
 * a single form field (WhatsApp — prefilled from their lookup) plus an
 * optional "anything to share?" note. Submitting logs a hires_requests row
 * so Rakesh can follow up personally.
 */

export default function RequestOriginals() {
  const { eventId } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const lookupId = (params.get("ref") || "").trim();

  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadErr, setLoadErr] = useState("");

  const [whatsapp, setWhatsapp] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitErr, setSubmitErr] = useState("");
  const [done, setDone] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadErr("");
    try {
      const url = new URL(`${API}/public/gallery/${eventId}/hires`);
      if (lookupId) url.searchParams.set("ref", lookupId);
      const { data } = await axios.get(url.toString());
      setMeta(data);
      if (data.whatsapp) setWhatsapp(data.whatsapp);
      if (data.already_requested) setDone(true);
    } catch (e) {
      setLoadErr(formatApiError(e.response?.data?.detail) || "This gallery isn't available.");
    } finally {
      setLoading(false);
    }
  }, [eventId, lookupId]);

  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitErr("");
    if (!whatsapp.trim() || whatsapp.replace(/\D/g, "").length < 8) {
      setSubmitErr("Please enter a valid WhatsApp number.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/public/gallery/${eventId}/request-originals`, {
        whatsapp: whatsapp.trim(),
        lookup_id: lookupId,
        name: name.trim(),
        email: email.trim(),
        message: message.trim(),
      });
      setDone(true);
    } catch (e2) {
      setSubmitErr(formatApiError(e2.response?.data?.detail) || e2.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    // iter147 — Skeleton loading matches the real form layout.
    return (
      <div className="min-h-screen bg-stone-50" data-testid="hires-loading" aria-busy="true">
        <div className="max-w-xl mx-auto px-4 sm:px-6 py-10 space-y-6">
          <div className="text-center space-y-3">
            <div className="kb-skeleton h-3 w-32 mx-auto" />
            <div className="kb-skeleton h-8 w-72 mx-auto max-w-full" />
            <div className="kb-skeleton h-3 w-56 mx-auto max-w-full" />
          </div>
          <div className="bg-white border border-stone-200 rounded-2xl p-6 shadow-sm space-y-4">
            <div className="kb-skeleton h-10 rounded-md" />
            <div className="kb-skeleton h-10 rounded-md" />
            <div className="kb-skeleton h-20 rounded-md" />
            <div className="kb-skeleton h-10 rounded-full w-40" />
          </div>
          <span className="sr-only">Loading…</span>
        </div>
      </div>
    );
  }
  if (loadErr || !meta) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6 bg-stone-50" data-testid="hires-error">
        <div className="max-w-md w-full text-center bg-white border border-stone-200 rounded-2xl p-8 shadow-sm kb-fade-in">
          <div className="mx-auto w-12 h-12 rounded-full bg-rose-50 border border-rose-200 flex items-center justify-center mb-4">
            <Sparkles className="w-5 h-5 text-rose-600" strokeWidth={1.6} aria-hidden="true" />
          </div>
          <div className="text-[10px] tracking-[0.35em] uppercase text-stone-500 mb-2 font-semibold">Kahani Studio</div>
          <div className="font-heading text-2xl font-semibold mb-2 text-stone-900">Unavailable</div>
          <p className="text-stone-500 text-sm leading-relaxed">{loadErr || "Please check your link and try again."}</p>
        </div>
      </div>
    );
  }

  const eventName = meta.event.couple_name || meta.event.name || "your album";

  return (
    <div className="min-h-screen bg-stone-50">
      <header className="bg-gradient-to-br from-amber-900 via-rose-900 to-stone-900 text-white">
        <div className="max-w-3xl mx-auto px-5 py-10 md:py-14">
          <button
            onClick={() => navigate(`/g/${eventId}`)}
            className="text-rose-100/80 hover:text-white text-xs inline-flex items-center gap-1 mb-4"
            data-testid="hires-back"
          >
            <ArrowLeft className="w-3 h-3" /> Back to the album
          </button>
          <div className="text-[11px] uppercase tracking-[0.3em] text-amber-200 font-semibold mb-3 inline-flex items-center gap-2">
            <Gem className="w-3.5 h-3.5" strokeWidth={2} /> High-resolution album
          </div>
          <h1 className="font-heading text-3xl md:text-5xl font-semibold tracking-tight">
            The full-resolution photos from {eventName}
          </h1>
          <div className="text-rose-100/80 text-sm mt-3 max-w-xl">
            The photos in the web album are optimised for quick sharing. The originals — sharp enough for wall prints,
            albums and framing — can be delivered directly by <span className="font-heading">Kahani by Rakesh Janardan</span>.
            Leave your details and Rakesh will personally reach out on WhatsApp with the options.
          </div>
          {meta.event.event_date && (
            <div className="text-rose-200 text-xs mt-3 font-mono">{fmtDate(meta.event.event_date)}</div>
          )}
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-5 py-8">
        {done ? (
          <div className="card p-8 text-center bg-white" data-testid="hires-thank-you">
            <div className="w-14 h-14 mx-auto rounded-full bg-emerald-50 border border-emerald-200 flex items-center justify-center mb-4">
              <CheckCircle2 className="w-7 h-7 text-emerald-700" strokeWidth={1.6} />
            </div>
            <h2 className="font-heading text-2xl font-semibold text-stone-900">Request received</h2>
            <p className="text-stone-500 text-sm mt-2 max-w-md mx-auto">
              Rakesh will reach out on WhatsApp shortly with the album options and pricing.
            </p>
            <button
              onClick={() => navigate(`/g/${eventId}`)}
              className="btn btn-outline btn-sm mt-6"
              data-testid="hires-back-to-album"
            >
              Back to the album
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-[1fr_260px] gap-6">
            <form onSubmit={submit} className="card p-6 bg-white space-y-4" data-testid="hires-form">
              <div>
                <h2 className="font-heading text-xl font-semibold text-stone-900">Get the full-resolution album</h2>
                <p className="text-xs text-stone-500 mt-1">Only WhatsApp is required — the rest is optional.</p>
              </div>

              <div>
                <label className="label">WhatsApp number *</label>
                <input
                  type="tel"
                  required
                  value={whatsapp}
                  onChange={(e) => setWhatsapp(e.target.value)}
                  placeholder="+91 98XXXXXXXX"
                  className="input"
                  data-testid="hires-whatsapp"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="label">Your name</label>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="How should we address you?"
                    className="input"
                    data-testid="hires-name"
                  />
                </div>
                <div>
                  <label className="label">Email <span className="text-stone-400 font-normal">(optional)</span></label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    className="input"
                    data-testid="hires-email"
                  />
                </div>
              </div>

              <div>
                <label className="label">Anything you'd like to add? <span className="text-stone-400 font-normal">(optional)</span></label>
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  rows={3}
                  className="input"
                  placeholder="e.g. Just the Haldi photos please, or all originals; needed for framing…"
                  maxLength={2000}
                  data-testid="hires-message"
                />
              </div>

              {submitErr && (
                <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">
                  {submitErr}
                </div>
              )}

              <div className="pt-2 flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                <button type="submit" className="btn btn-primary" disabled={submitting} data-testid="hires-submit">
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" strokeWidth={1.6} />}
                  {submitting ? "Sending…" : "Request the full album"}
                </button>
                <div className="text-[11px] text-stone-400">
                  <Sparkles className="w-3 h-3 inline-block mr-1 text-rose-700" /> Rakesh usually replies within a business day.
                </div>
              </div>
            </form>

            <aside className="card p-5 bg-white text-sm space-y-3 text-stone-600">
              <div className="text-[11px] uppercase tracking-wider text-stone-500 font-semibold">What you'll get</div>
              {meta.match_count != null && meta.match_count > 0 && (
                <div className="flex items-start gap-2">
                  <span className="mt-0.5 inline-block w-1.5 h-1.5 rounded-full bg-rose-600" />
                  <span>Every one of the <strong>{meta.match_count}</strong> photo{meta.match_count === 1 ? "" : "s"} you were matched to, at full resolution.</span>
                </div>
              )}
              <div className="flex items-start gap-2">
                <span className="mt-0.5 inline-block w-1.5 h-1.5 rounded-full bg-rose-600" />
                <span>Print-ready RAW conversions — perfect for wall art & albums.</span>
              </div>
              <div className="flex items-start gap-2">
                <span className="mt-0.5 inline-block w-1.5 h-1.5 rounded-full bg-rose-600" />
                <span>Personalised recommendations — album layouts, framing, extended license.</span>
              </div>
              <div className="border-t border-stone-100 pt-3 mt-3 text-[11px] text-stone-400">
                <MessageCircle className="w-3 h-3 inline-block mr-1" /> All conversations happen over WhatsApp.
              </div>
            </aside>
          </div>
        )}
      </main>

      <footer className="max-w-3xl mx-auto px-5 pb-10 pt-6 text-center text-xs text-stone-400">
        Album photographed by <span className="font-heading text-stone-600">Kahani by Rakesh Janardan</span>
      </footer>
    </div>
  );
}
